// THE PINS WE DRAW RATHER THAN LOAD.
//
// A van pointing north-east is a fixed picture and lives in public/static/maps.
// A driver with AF on their chest is not: there is no file for it and there
// could not be, because the picture depends on the record. So a shape, a fill
// and up to two letters arrive from the server - see LiveMap::Marker - and these
// build the SVG.
//
// HERE RATHER THAN IN ONE MAP, for the reason GREYSCALE_STYLES is here: two maps
// now draw these pins. The viewport map through live_map_controller, and the
// focused team's map on the live board through LiveApp/RoundMap. A shop has to
// be the same picture on both screens - two copies of this table is one of them
// being tuned and the other quietly diverging, and a reader who has learnt that
// a triangle with letters on it is a charity's shop has learnt it for every map
// or for none.
//
// WHAT THE COLOUR MEANS IS THE CALLER'S, and the two differ on purpose. The
// viewport map fills a shop by its service status, because that map is a
// directory; the live board fills the same shape by what became of the stop,
// because that map is about today. The SHAPE says what kind of thing it is and
// the fill says what is true of it, which is why only the shape is shared.

// The text colour is never passed in: black-or-white against the fill is
// arithmetic and this is where the fill is.
//
// `body` is drawn inside a group carrying the black outline; the badge is drawn
// after it, unstroked. Both anchor at the bottom centre, so a shop's point and a
// person's feet sit on the coordinate, which is where the file-based icons put
// themselves too.
//
// SIZED BY WHAT THE BADGE NEEDS, not by what the old icons were. At the 24px the
// van discs use, two bold letters are a smudge - drawn out at six times and then
// looked at back at 1:1, the first attempt was unreadable in every fill. These
// are 32 across, which is what it takes.
export const MARKER_SHAPES = {
  // Head and shoulders, initials across the chest - which is where the width is,
  // and the only part of a person-shaped pin that can hold two letters.
  person: {
    w: 32, h: 40, badgeAt: [16, 33], badgeSize: 13,
    body: fill => `<circle cx="16" cy="7" r="5" fill="${fill}"/>` +
      `<path d="M3 39C3 26 9 15.5 16 15.5S29 26 29 39Z" fill="${fill}"/>`,
  },
  // The same downward triangle the shop pins have always been. The badge sits in
  // the upper third because that is where a triangle standing on its point is
  // wide enough to hold it.
  shop: {
    w: 32, h: 36, badgeAt: [16, 16], badgeSize: 12,
    body: fill => `<path d="M16 34.5 1 5h30Z" fill="${fill}"/>`,
  },
}

// HOW BIG EVERY PIN IS DRAWN, as a multiple of its natural size.
//
// ONE KNOB FOR A WHOLE MAP, drawn pins and file ones together, so that they keep
// their relative weight - shrinking only the badged ones would leave a 22px shop
// beside a 24px van, which reads as a mistake rather than as a scale.
//
// The badges are laid out at full size in MARKER_SHAPES and then scaled down
// with everything else, which is what makes this a size and not a redesign. It
// is also the limit on it: the letters are legible at 0.7 and were a smudge when
// the shapes themselves were built small, so a map that wants smaller pins wants
// unbadged ones.
export const MARKER_SCALE = 0.7

// The files in public/static/maps are all 24 square. Stated because a scale has
// to be a scale OF something, and an <img> Google has not loaded yet cannot be
// asked how big it is.
export const FILE_ICON_SIZE = 24

// ONE BUILT ICON PER SHAPE-FILL-BADGE-SCALE, not one per marker: a team of
// fifteen drivers is fifteen markers and a handful of distinct pictures, and the
// focused team's map rebuilds its pins every time the roads are re-read.
const built = new Map()

// A google.maps.Icon for a drawn pin, or null for a shape we do not have.
//
// `google` has to be loaded, which it is wherever a marker is being made: both
// callers build icons only after loadGoogleMaps has resolved.
export function drawnMarkerIcon({ shape, color, badge }, { scale = MARKER_SCALE } = {}) {
  const spec = MARKER_SHAPES[shape]
  if (!spec) return null

  const fill = color || '#9e9e9e'
  const key = `${shape}|${fill}|${badge || ''}|${scale}`
  if (built.has(key)) return built.get(key)

  const text = badge
    ? `<text x="${spec.badgeAt[0]}" y="${spec.badgeAt[1]}" text-anchor="middle"` +
      ` font-family="system-ui, -apple-system, sans-serif" font-size="${spec.badgeSize}"` +
      ` font-weight="700" fill="${readableOn(fill)}">${escapeForSvg(badge)}</text>`
    : ''

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${spec.w}" height="${spec.h}"` +
    ` viewBox="0 0 ${spec.w} ${spec.h}">` +
    `<g stroke="#000000" stroke-width="1.25" stroke-linejoin="round">${spec.body(fill)}</g>` +
    `${text}</svg>`

  const icon = placedIcon(`data:image/svg+xml,${encodeURIComponent(svg)}`, spec.w, spec.h, scale)
  built.set(key, icon)
  return icon
}

// Scaled, and anchored at the bottom centre - which is where Google puts a bare
// url icon by default, so scaling one does not also move it. A shop's point and
// a person's feet land on the coordinate; the van discs sit just above theirs,
// which is how those files were drawn to be placed.
export function placedIcon(url, w, h, scale = MARKER_SCALE) {
  return {
    url,
    scaledSize: new google.maps.Size(w * scale, h * scale),
    anchor: new google.maps.Point((w * scale) / 2, h * scale),
  }
}

// Black or white, whichever can be read on the fill. Passed in from nowhere
// because it is arithmetic on a colour this file already has: black shops want
// white letters, yellow and light grey ones want black, and a colour added later
// gets the right answer without anybody remembering to state it.
//
// WCAG relative luminance, with the crossover at 0.4 rather than the usual 0.18
// - a badge is bold text at nine pixels over a saturated fill, and erring
// towards black is erring towards the more readable of the two.
export function readableOn(hex) {
  const n = parseInt(String(hex).replace('#', ''), 16)
  if (Number.isNaN(n)) return '#000000'

  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(channel => {
    const s = channel / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  })

  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.4 ? '#000000' : '#ffffff'
}

// BY HAND RATHER THAN THROUGH THE DOM, which is what the map controller does for
// the words in a popup. A badge is two letters chosen by a layer, so this is a
// belt on a record nobody types into - but it goes inside an SVG that is then
// handed to the browser as a url, and a module that can be tested without a
// document is worth more here than reusing a one-liner that needs one.
function escapeForSvg(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
